import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/SimpleAuthContext';
import { announcementsApi } from '../services/announcementsApi';
import API_CONFIG from '../config/apiConfig';
import { CAREER_PASSPORT_DEFINITIONS, getCoursePassportLinks } from '../config/careerPassports';
import learnerAvif480 from '../assets/landing/career-learner-480.avif';
import learnerAvif800 from '../assets/landing/career-learner-800.avif';
import learnerAvif1280 from '../assets/landing/career-learner-1280.avif';
import learnerWebp480 from '../assets/landing/career-learner-480.webp';
import learnerWebp800 from '../assets/landing/career-learner-800.webp';
import learnerWebp1280 from '../assets/landing/career-learner-1280.webp';
import {
  AcademicCapIcon, ArrowPathIcon, ArrowRightIcon, BellIcon,
  BookOpenIcon, BuildingOfficeIcon, ClockIcon, LightBulbIcon,
  MapIcon, UserGroupIcon
} from '@heroicons/react/24/outline';
import './Home.css';

const learnerImageSizes = '(max-width: 640px) calc(100vw - 36px), (max-width: 950px) 640px, (max-width: 1280px) 52vw, 627px';

const features = [
  { icon: LightBulbIcon, title: 'Discover your direction', description: 'Explore your interests and skills through the career assessment, then review your recommended training pathways.' },
  { icon: MapIcon, title: 'Find your passport pathway', description: 'Understand how courses connect to Skills Passports, with belt and standard targets shown where they are mapped.' },
  { icon: BookOpenIcon, title: 'Choose your training', description: 'Compare course requirements, campus options and online learning before choosing your next step.' },
  { icon: AcademicCapIcon, title: 'Follow your progress', description: 'Manage applications and documents, access your learning and track your progress from your dashboard.' }
];

const formatDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
};

const FeaturedCourse = ({ course }) => {
  const passports = getCoursePassportLinks(course);
  const duration = Number(course.duration_months);
  const intake = formatDate(course.next_intake?.course_start_date);
  const capacity = Number(course.max_capacity);
  const available = Number(course.available_capacity);
  const showCapacity = !course.is_lms_hosted && capacity > 0 && Number.isFinite(available);
  const percent = showCapacity ? Math.min(100, Math.max(0, available / capacity * 100)) : 0;

  return (
    <article className="home-course">
      <div className="home-course-labels">
        <span className="home-course-mode">{course.is_lms_hosted ? 'Online learning' : 'Campus training'}</span>
        {course.nqf_exit_level ? <span className="home-course-nqf">NQF {course.nqf_exit_level}</span> : null}
      </div>
      <h3><Link to={`/courses/${course.id}`}>{course.programme_name}</Link></h3>
      {passports.length > 0 ? (
        <div className="home-course-passports" aria-label="Skills Passport pathways">
          {passports.map(passport => (
            <span key={passport.id} className={`home-course-passport home-course-passport--${passport.tone}`} title={passport.coverage}>
              {passport.title}
            </span>
          ))}
        </div>
      ) : null}
      <dl className="home-course-details">
        <div><dt><BuildingOfficeIcon aria-hidden="true" /><span className="home-sr-only">Location</span></dt><dd>{course.is_lms_hosted ? 'Learn online in the LMS' : course.campus?.name || 'Location to be confirmed'}</dd></div>
        {duration > 0 ? <div><dt><ClockIcon aria-hidden="true" /><span className="home-sr-only">Duration</span></dt><dd>{duration} {duration === 1 ? 'month' : 'months'}</dd></div> : null}
        {showCapacity ? <div><dt><UserGroupIcon aria-hidden="true" /><span className="home-sr-only">Availability</span></dt><dd>{available} of {capacity} places available</dd></div> : null}
      </dl>
      {showCapacity ? <div className="home-course-capacity" role="progressbar" aria-label="Available places" aria-valuenow={Math.round(percent)} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${percent}%` }} /></div> : null}
      <div className="home-course-bottom">
        <p>{course.is_lms_hosted ? 'Learn through your linked LMS account' : intake ? `Next intake: ${intake}` : 'View course and intake details'}</p>
        <Link to={`/courses/${course.id}`} className="home-button home-button--secondary">Learn More<ArrowRightIcon aria-hidden="true" /></Link>
      </div>
    </article>
  );
};

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState(false);
  const [featuredAnnouncements, setFeaturedAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState(false);
  const [totalCourses, setTotalCourses] = useState(null);

  const fetchFeaturedCourses = useCallback(async (signal) => {
    setCoursesLoading(true);
    setCoursesError(false);
    try {
      const response = await fetch(API_CONFIG.courses.randomFeatured(), { signal });
      if (!response.ok) throw new Error('Unable to load featured courses');
      const data = await response.json();
      const courses = data.results || (Array.isArray(data) ? data : []);
      setFeaturedCourses(Array.from(new Map(courses.map(course => [course.id, course])).values()).slice(0, 3));
    } catch (error) {
      if (error.name !== 'AbortError') setCoursesError(true);
    } finally {
      if (!signal?.aborted) setCoursesLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchFeaturedCourses(controller.signal);

    const fetchCatalogCount = async () => {
      try {
        const response = await fetch(API_CONFIG.courses.list(), { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        const count = Array.isArray(data) ? data.length : data.count;
        if (Number.isFinite(count)) setTotalCourses(count);
      } catch {
        // Catalog navigation remains available if the count cannot be loaded.
      }
    };

    const fetchAnnouncements = async () => {
      try {
        const result = await announcementsApi.getFeaturedAnnouncements();
        if (controller.signal.aborted) return;
        if (!result.success) throw new Error('Unable to load announcements');
        const announcements = result.data?.results || result.data;
        setFeaturedAnnouncements(Array.isArray(announcements) ? announcements : []);
      } catch {
        if (!controller.signal.aborted) setAnnouncementsError(true);
      } finally {
        if (!controller.signal.aborted) setAnnouncementsLoading(false);
      }
    };

    fetchCatalogCount();
    fetchAnnouncements();
    return () => controller.abort();
  }, [fetchFeaturedCourses]);

  return (
    <div className="home-page home-polished">
      <section className="home-intro" aria-labelledby="home-title">
        <div className="home-container home-intro-content">
          <div className="home-intro-copy">
            <h1 id="home-title">GMSP3 <span>Career Guidance</span></h1>
            <p className="home-intro-tagline">Empowering skills, building futures.</p>
            <p className="home-intro-description">Discover your strengths, find a Skills Passport pathway and explore training that helps you take your next career step.</p>
            <div className="home-actions">
              <Link to="/courses" className="home-button home-button--primary">Explore Programs<ArrowRightIcon aria-hidden="true" /></Link>
              <Link to="/career" className="home-button home-button--secondary">Find my career direction<LightBulbIcon aria-hidden="true" /></Link>
            </div>
            {!isAuthenticated ? <p className="home-intro-account">New here? <Link to="/register">Create your account</Link> to save your journey.</p> : null}
          </div>
          <figure className="home-intro-image">
            <picture>
              <source
                type="image/avif"
                srcSet={`${learnerAvif480} 480w, ${learnerAvif800} 800w, ${learnerAvif1280} 1280w`}
                sizes={learnerImageSizes}
              />
              <img
                src={learnerWebp800}
                srcSet={`${learnerWebp480} 480w, ${learnerWebp800} 800w, ${learnerWebp1280} 1280w`}
                sizes={learnerImageSizes}
                width="1536"
                height="1024"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                alt="An African female learner studying online on a laptop and looking thoughtfully toward the window."
              />
            </picture>
          </figure>
        </div>
      </section>

      <section className="home-facts" aria-label="Training and passport pathways">
        <div className="home-container home-facts-grid">
          <div><strong>{totalCourses ?? '—'}</strong><span>Training programs</span></div>
          <div><strong>{CAREER_PASSPORT_DEFINITIONS.length}</strong><span>Skills Passport tracks</span></div>
          <div><strong>8</strong><span>Belt levels</span></div>
          <div><strong>3</strong><span>Progression standards</span></div>
        </div>
      </section>

      <section className="home-catalog" aria-labelledby="home-catalog-title">
        <div className="home-container">
          <div className="home-heading-row">
            <div className="home-heading"><h2 id="home-catalog-title">Explore your next opportunity</h2><p>A selection of training programs from the course catalog.</p></div>
            <button type="button" className="home-button home-button--secondary" onClick={() => fetchFeaturedCourses()} disabled={coursesLoading}>
              <ArrowPathIcon className={coursesLoading ? 'home-spin' : ''} aria-hidden="true" />{coursesLoading ? 'Refreshing…' : 'Refresh selection'}
            </button>
          </div>
          {coursesError ? <div className="home-status" role="status">We couldn’t refresh the courses. Try again or <Link to="/courses">browse the course catalog</Link>.</div> : null}
          {coursesLoading && featuredCourses.length === 0 ? (
            <div className="home-course-grid" aria-busy="true" aria-label="Loading training programs">
              {[0, 1, 2].map(item => <div key={item} className="home-course home-course-placeholder" aria-hidden="true"><span /><span /><span /></div>)}
            </div>
          ) : featuredCourses.length > 0 ? (
            <div className="home-course-grid" aria-busy={coursesLoading}>{featuredCourses.map(course => <FeaturedCourse key={course.id} course={course} />)}</div>
          ) : !coursesError ? <div className="home-status">Featured programs will appear here when they are available. You can still browse the full catalog.</div> : null}
          <div className="home-catalog-link"><Link to="/courses">View All Programs<ArrowRightIcon aria-hidden="true" /></Link></div>
        </div>
      </section>

      <section className="home-notices" aria-labelledby="home-notices-title">
        <div className="home-container">
          <div className="home-heading"><h2 id="home-notices-title">Latest announcements</h2><p>News, deadlines and updates for your learning journey.</p></div>
          {announcementsLoading ? <div className="home-status" role="status">Loading announcements…</div> : featuredAnnouncements.length > 0 ? (
            <div className="home-notice-grid">
              {featuredAnnouncements.slice(0, 6).map(announcement => (
                <article key={announcement.id} className="home-notice">
                  <div className="home-notice-top"><BellIcon aria-hidden="true" /><span className={announcement.is_urgent ? 'home-notice-urgent' : ''}>{announcement.is_urgent ? 'Urgent' : announcement.category_display || 'News'}</span></div>
                  <h3>{announcement.title}</h3><p>{announcement.content}</p>
                  <div className="home-notice-meta"><time dateTime={announcement.publish_date || announcement.created_at}>{formatDate(announcement.publish_date || announcement.created_at)}</time>{announcement.target_course ? <span>Course update</span> : null}</div>
                </article>
              ))}
            </div>
          ) : (
            <div className="home-notice-empty" role="status"><BellIcon aria-hidden="true" /><div><h3>{announcementsError ? 'Updates are temporarily unavailable' : 'You’re all caught up'}</h3><p>{announcementsError ? 'Please check back for the latest announcements.' : 'New announcements and important dates will appear here.'}</p></div></div>
          )}
        </div>
      </section>

      <section className="home-benefits" aria-labelledby="home-benefits-title">
        <div className="home-container">
          <div className="home-heading"><h2 id="home-benefits-title">A clearer path from skills to opportunity</h2><p>Everything you need to plan, choose and follow your next step.</p></div>
          <div className="home-benefit-grid">{features.map(({ icon: Icon, title, description }) => <div className="home-benefit" key={title}><Icon aria-hidden="true" /><h3>{title}</h3><p>{description}</p></div>)}</div>
        </div>
      </section>

      <section className="home-start" aria-labelledby="home-start-title">
        <div className="home-container home-start-content"><div><h2 id="home-start-title">Ready to take the next step?</h2><p>{isAuthenticated ? 'Pick up your learning journey and manage your progress in one place.' : 'Create your profile, explore your interests and start building your learning journey.'}</p></div><div className="home-actions"><Link to={isAuthenticated ? '/dashboard' : '/register'} className="home-button home-button--primary">{isAuthenticated ? 'Go to Dashboard' : 'Create your account'}<ArrowRightIcon aria-hidden="true" /></Link>{!isAuthenticated ? <Link to="/login" className="home-button home-button--secondary">Sign in</Link> : null}</div></div>
      </section>
    </div>
  );
};

export default Home;


